import type { AdminCustomJoinPage } from "../../types/customJoinPage";
import axios from "axios";
import { adminApi } from "../../lib/axios";

// Types for Brand Management
export interface BrandNiche {
  id: string;
  name: string;
  status: "ACTIVE" | "INACTIVE";
}

export interface BrandSocialMedia {
  platform:
    | "TIKTOK"
    | "INSTAGRAM"
    | "YOUTUBE"
    | "FACEBOOK"
    | "DISCORD"
    | "TWITTER"
    | "LINKEDIN";
  username: string;
}

export interface BrandProgram {
  id: string;
  commissionType: "PERCENTAGE" | "FIXED";
  commissionValue: string;
  discountValue: string;
  status: "ACTIVE" | "INACTIVE";
}

export interface BrandPlan {
  id: string;
  name: string;
  price: number;
}

export interface BrandStats {
  totalSales: number;
  totalCommissions: number;
  totalAffiliates: number;
  revenueRank: number;
  commissionRank: number;
  ordersRank: number;
  affiliatesRank: number;
  overallRank: number;
  previousOverallRank?: number | null;
}

/** Levels of white-label access. Mirrors the backend enum. */
export type WhiteLabelTier = "NONE" | "BASIC" | "FULL";

export interface AdminBrand {
  id: string;
  sessionId: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  companyName: string;
  website: string;
  country: string;
  profileImage: string;
  brandBannerImage: string;
  brandProfileImage: string;
  description: string;
  timezone: string | null;
  onboardingStatus: "PENDING" | "IN_PROGRESS" | "COMPLETED";
  isTestAccount: boolean;
  isPartner?: boolean;
  isActive: boolean;
  isEmailAutomationEnabled?: boolean;
  /** Ranking pin: true holds all five rank columns through the scheduled recompute. */
  rankPinned?: boolean;
  /** Explicit pinned position; null = hold the current overallRank. */
  pinnedRank?: number | null;
  rankPinnedAt?: string | null;
  /** The brand's own white-label switch. Whether it takes effect also depends
   *  on the brand's plan — see whiteLabelAdminTierOverride. */
  whiteLabelEnabledByBrand?: boolean;
  /** Admin override of the plan-derived tier: null follows the plan. */
  whiteLabelAdminTierOverride?: WhiteLabelTier | null;
  /** Verified per-brand From address for branded emails; null = the platform's
   *  white-label sender with the brand's display name. */
  whiteLabelEmailFromAddress?: string | null;
  /** Brand-authored join page: admin switch + what the brand did with it.
   *  Null when the feature was never enabled for this brand. */
  customJoinPage?: AdminCustomJoinPage | null;
  /** PRE_RELEASE_TIKTOK: admin-granted early access to the TikTok Shop surfaces. */
  tiktokPreRelease?: boolean;
  tiktokPreReleaseAt?: string | null;
  joinedAt: string;
  niches: BrandNiche[];
  socialMedia: BrandSocialMedia[];
  program: BrandProgram | null;
  plan: BrandPlan | null;
  stats: BrandStats;
}

export interface SearchBrandsResponse {
  data: AdminBrand[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Search parameters interface
export interface BrandSearchParams {
  page?: number;
  limit?: number;
  searchBy?: "COMPANY" | "EMAIL" | "USERNAME" | "WEBSITE";
  keyword?: string;
  minSales?: number;
  maxSales?: number;
  minCommissions?: number;
  maxCommissions?: number;
  joinedAfter?: string; // ISO date string
  joinedBefore?: string; // ISO date string
  sortBy?: "SALES" | "COMMISSIONS" | "AFFILIATES" | "COMPANY" | "JOIN_DATE";
  sortOrder?: "ASC" | "DESC";
  status?: "ACTIVE" | "INACTIVE";
  onboardingStatus?: "PENDING" | "IN_PROGRESS" | "COMPLETED";
  country?: string;
  hasProgram?: boolean;
  rankPinned?: boolean;
  /** PRE_RELEASE_TIKTOK: true = pilot brands only, false = everyone else. */
  tiktokPreRelease?: boolean;
}

/** PRE_RELEASE_TIKTOK: what the bulk paste-emails action did. */
export interface TikTokPreReleaseBulkResult {
  enabled: Array<{ brandId: string; email: string; companyName: string | null }>;
  alreadyEnabled: Array<{ brandId: string; email: string; companyName: string | null }>;
  unmatched: string[];
}

// Service class
export class AdminBrandsService {
  // Search brands with advanced filtering and pagination
  async searchBrands(
    params: BrandSearchParams = {}
  ): Promise<SearchBrandsResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      // Add search parameters
      if (params.searchBy) queryParams.append("searchBy", params.searchBy);
      if (params.keyword) queryParams.append("keyword", params.keyword);

      // Add filter parameters
      if (params.minSales !== undefined)
        queryParams.append("minSales", params.minSales.toString());
      if (params.maxSales !== undefined)
        queryParams.append("maxSales", params.maxSales.toString());
      if (params.minCommissions !== undefined)
        queryParams.append("minCommissions", params.minCommissions.toString());
      if (params.maxCommissions !== undefined)
        queryParams.append("maxCommissions", params.maxCommissions.toString());
      if (params.joinedAfter)
        queryParams.append("joinedAfter", params.joinedAfter);
      if (params.joinedBefore)
        queryParams.append("joinedBefore", params.joinedBefore);
      if (params.status) queryParams.append("status", params.status);
      if (params.onboardingStatus)
        queryParams.append("onboardingStatus", params.onboardingStatus);
      if (params.country) queryParams.append("country", params.country);
      if (params.rankPinned !== undefined)
        queryParams.append("rankPinned", params.rankPinned.toString());
      if (params.hasProgram !== undefined)
        queryParams.append("hasProgram", params.hasProgram.toString());
      if (params.tiktokPreRelease !== undefined)
        queryParams.append("tiktokPreRelease", params.tiktokPreRelease.toString());

      // Add sorting parameters
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await adminApi.get(
        `/brand/admin/search?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search brands"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Get brand by ID (useful for detailed view)
  async getBrandById(id: string): Promise<AdminBrand> {
    try {
      const response = await adminApi.get(`/brand/find-one/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch brand details"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Update brand status
  async updateBrandStatus(id: string, isActive: boolean): Promise<void> {
    try {
      await adminApi.patch(`/brand/${id}/status`, { isActive });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update brand status"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Helper method to format brand data for display
  formatBrandForDisplay(brand: AdminBrand) {
    const fullName = `${brand.firstName} ${brand.lastName}`;

    return {
      id: brand.id,
      name: fullName,
      companyName: brand.companyName,
      email: brand.email,
      username: brand.username || "Not set",
      website: brand.website,
      profileImage: brand.profileImage,
      brandProfileImage: brand.brandProfileImage,
      brandBannerImage: brand.brandBannerImage,
      country: brand.country,
      joinedAt: new Date(brand.joinedAt).toLocaleDateString(),
      onboardingStatus: brand.onboardingStatus,
      isActive: brand.isActive,
      totalSales: brand.stats.totalSales,
      totalCommissions: brand.stats.totalCommissions,
      totalAffiliates: brand.stats.totalAffiliates,
      overallRank: brand.stats.overallRank,
      nichesCount: brand.niches.length,
      socialMediaCount: brand.socialMedia.length,
      hasActiveProgram: brand.program?.status === "ACTIVE" || false,
      commissionType: brand.program?.commissionType || "Not set",
      commissionValue: brand.program?.commissionValue || "Not set",
      discountValue: brand.program?.discountValue || "Not set",
    };
  }

  // Helper method to get brand statistics using new admin overview endpoint
  async getBrandStats(): Promise<{
    totalBrands: number;
    activeBrands: number;
    totalSales: number;
    averageSalesPerBrand: number;
  }> {
    try {
      const response = await adminApi.get("/brand/admin/dashboard-overview");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch brand stats:", error);
      return {
        totalBrands: 0,
        activeBrands: 0,
        totalSales: 0,
        averageSalesPerBrand: 0,
      };
    }
  }

  async toggleTestAccount(brandId: string, isTestAccount: boolean): Promise<{brandId: string, isTestAccount: boolean, status: string}> {
    try {
     const response = await adminApi.put(`/brand/admin/test-account`, {
        brandId,
        isTestAccount,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to toggle test account");
    }
  }

  async togglePartnerAccount(brandId: string, isPartner: boolean): Promise<{brandId: string, isPartner: boolean, status: string}> {
    try {
     const response = await adminApi.put(`/brand/admin/partner-account`, {
        brandId,
        isPartner,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to toggle partner account");
    }
  }
  /** PRE_RELEASE_TIKTOK: the per-brand switch. */
  async setTikTokPreRelease(
    brandId: string,
    enabled: boolean,
  ): Promise<{ brandId: string; enabled: boolean; enabledAt: string | null }> {
    const response = await adminApi.put(`/brand/admin/tiktok-pre-release`, { brandId, enabled });
    return response.data;
  }

  /** PRE_RELEASE_TIKTOK: enable every brand whose login email is in the list. */
  async bulkEnableTikTokPreRelease(emails: string[]): Promise<TikTokPreReleaseBulkResult> {
    const response = await adminApi.post(`/brand/admin/tiktok-pre-release/bulk`, { emails });
    return response.data;
  }

  async getFilteringBrands(): Promise<{id:string, name:string, programId:string}[]> {
    const response = await adminApi.get("brand/admin/minimal");
    return response.data;
  }

}

// Export singleton instance
export const adminBrandsService = new AdminBrandsService();
